import React, { useMemo } from 'react';
import { ActiveFilter, FilterRendererProps } from '../../types';
import { searchSelectedOrgs, searchSubmitters } from '../../../../../Api';
import ChildFilterPanel from '../../../../../pages/filter-bar/ChildFilterPanel';
import { formatOrgFilterChipLabel } from '../../../../../utils/company-filter-label';
import { getOrgExtraInfoFieldsForFilter, ORG_EXTRA_INFO_FIELDS_LIMIT_FILTER_UI } from '../../../../../utils/org-filter-config';


const FetchableCheckboxFilterForModal: React.FC<FilterRendererProps> = ({
  filterConfig,
  filterName,
  localActiveFilters,
  setLocalActiveFilters,
  onCountUpdate,
  clearTrigger,
  filtersConfig,
}) => {
  const filterObject = filterConfig.filterObject || 'evidence';
  const orgExtraInfoFields = useMemo(
    () => getOrgExtraInfoFieldsForFilter(filterConfig, filtersConfig, ORG_EXTRA_INFO_FIELDS_LIMIT_FILTER_UI),
    [filterConfig, filtersConfig]
  );

  const existingFilter = localActiveFilters.find(
    (filter: any) => filter.name === filterName && filter.filterObject === filterObject
  );
  const selectedIds = existingFilter?.selected || [];

  const buildOptionLabel = (item: { _id: string; name?: string; [key: string]: unknown }) =>
    orgExtraInfoFields.length > 0
      ? formatOrgFilterChipLabel(item, orgExtraInfoFields)
      : (item.name ?? item._id);

  const handleApply = (selectedItems: any[]) => {
    const selectedIds = selectedItems.map(item => item._id);

    const optionsMap: { [key: string]: string } = {};
    selectedItems.forEach(item => {
      optionsMap[item._id] = buildOptionLabel(item);
    });

    const updatedFilter: ActiveFilter = {
      ...filterConfig,
      name: filterName,
      filterObject: filterObject as 'evidence' | 'idea',
      selected: selectedIds,
      query: null,
      options: optionsMap,
    };

    setLocalActiveFilters((prev : any) => {
      const existingIndex = prev.findIndex(
        (filter: any) => filter.name === filterName && filter.filterObject === filterObject
      );

      let updatedFilters;
      if (existingIndex >= 0) {
        if (selectedIds.length === 0) {
          updatedFilters = prev.filter((_ : any, index: number) => index !== existingIndex);
        } else {
          updatedFilters = [...prev];
          updatedFilters[existingIndex] = updatedFilter;
        }
      } else if (selectedIds.length > 0) {
        updatedFilters = [...prev, updatedFilter];
      } else {
        updatedFilters = prev;
      }

      return updatedFilters;
    });

    onCountUpdate?.(selectedIds.length);
  };

  const handleClear = () => {
    setLocalActiveFilters((prev: any) =>
      prev.filter((filter: any) => !(filter.name === filterName && filter.filterObject === filterObject))
    );
    onCountUpdate?.(0);
  };

  const getSearchApiFunction = () => {
    if (filterConfig.dbFieldPath?.includes('submitterId')) {
      return async (params: { query: string; ids?: string[] }, auth0: any) => {
        const response = await searchSubmitters(params, auth0);
        return response.submitters || [];
      };
    }
    return async (params: { query: string; ids?: string[] }, auth0: any) => {
      const response = await searchSelectedOrgs(params, auth0);
      return response.accounts || [];
    };
  };

  return (
    <ChildFilterPanel
      key={filterName}
      searchPlaceholder={`Search ${filterConfig.label || filterName}`}
      onApply={handleApply}
      onClear={handleClear}
      searchApiFunction={getSearchApiFunction()}
      loadInitialData={true}
      setLocalActiveFilters={setLocalActiveFilters}
      localActiveFilters={localActiveFilters}
      filterConfig={filterConfig}
      filterName={filterName}
      filterObject={filterObject}
      clearTrigger={clearTrigger}
      onCountUpdate={onCountUpdate}
      ideasFiltersConfig={filtersConfig}
    />
  );
};

export default FetchableCheckboxFilterForModal;
